import type { ResourceMembership } from 'v5/core/entity-schema/entitySchema.types';
import { contactDetailColumnByType } from 'v5/core/entity-schema/entitySchema.types';
import { valueScopeUtils } from 'v5/core/form-schema/formSchema.types';
import type {
  ResourcePatch,
  ResourcePatchOf
} from 'v5/core/entity-values/resourcePatch.types';
import {
  applyCoreChanges,
  assertPersistedId,
  customFieldRowWithoutId,
  locationRow,
  rateValueColumns
} from './bodyShared';
import type {
  ContactCreateBody,
  ContactMembershipBodyRow,
  GroupBodyRow,
  TagBodyRow
} from './contactBody.types';
import type { CreateResourceInput } from './ResourceWriteModel';

/**
 * Pure: `CreateResourceInput` → one `POST /contacts` body. The create graph
 * inserts every contact-owned child, so a patch that carries a persisted id
 * on such a child is a caller bug and throws. Two patches addressing the same
 * write target (see `patchIdentity`) throw as well.
 *
 * Membership (`enterprise_workspaces`) is accepted by `createProxy` (same
 * `allowGraph` as PUT, and the membership guard lives only in
 * `UpdateModel.js`); `[{ enterprise_workspace_id: null }]` is the global row
 * per `ContactWorkspaceMembership.jsonSchema`.
 */
export function buildCreateBody(input: CreateResourceInput): ContactCreateBody {
  const body: ContactCreateBody = {
    resource_type_id: input.resourceTypeId,
    enterprise_workspaces: membershipRows(input.membership)
  };
  if (input.profilePicture !== null) {
    body.profile_picture = input.profilePicture;
  }

  const seen = new Set<string>();
  for (const patch of input.patches) {
    for (const identity of patchIdentities(patch)) {
      if (seen.has(identity)) {
        throw new Error(`buildCreateBody: duplicate patch for ${identity}`);
      }
      seen.add(identity);
    }
    applyPatch(body, patch, input.resourceTypeId);
  }
  return body;
}

export function membershipRows(
  membership: ResourceMembership
): ContactMembershipBodyRow[] {
  switch (membership.type) {
    case 'hidden':
      return [];
    case 'global':
      return [{ enterprise_workspace_id: null }];
    case 'workspaces':
      return Array.from(new Set(membership.workspaceIds)).map(id => ({
        enterprise_workspace_id: assertPersistedId(
          id,
          'buildCreateBody(enterprise_workspaces)'
        )
      }));
  }
}

/**
 * Write targets of a patch: one per core column (several core units — name,
 * pronouns, role — each emit their own `core` patch), one per relation, per
 * detail type, and per (definition, scope) for custom fields — the same
 * definition legitimately appears once per scope (global value + workspace
 * override).
 */
function patchIdentities(patch: ResourcePatch): string[] {
  switch (patch.kind) {
    case 'core':
      return patch.changes.map(change => `core:${change.column}`);
    case 'tags':
    case 'groups':
    case 'locations':
    case 'rates':
      return [patch.kind];
    case 'contact-details':
      return [`${patch.kind}:${patch.detailType}`];
    case 'custom-field':
    case 'custom-field-group':
      return [
        `${patch.kind}:${patch.definitionId}@${valueScopeUtils.sectionId(patch.scope)}`
      ];
  }
}

function applyPatch(
  body: ContactCreateBody,
  patch: ResourcePatch,
  resourceTypeId: number
): void {
  switch (patch.kind) {
    case 'core':
      applyCoreChanges(body, patch.changes);
      return;
    case 'contact-details':
      applyContactDetails(body, patch);
      return;
    case 'tags':
      body.tags = tagRows(patch);
      return;
    case 'groups':
      body.groups = groupRows(patch, resourceTypeId);
      return;
    case 'locations':
      body.locations = [
        ...patch.existingIds.map(id => ({
          id: assertPersistedId(id, 'buildCreateBody(locations)')
        })),
        ...patch.create.map(locationRow)
      ];
      return;
    case 'rates':
      applyRates(body, patch);
      return;
    case 'custom-field':
      applyCustomField(body, patch);
      return;
    case 'custom-field-group':
      applyCustomFieldGroup(body, patch);
      return;
  }
}

function assertNoPersistedId(id: number | null, context: string): void {
  if (id !== null) {
    throw new Error(
      `${context}: a create body cannot carry persisted id ${String(id)}`
    );
  }
}

function applyContactDetails(
  body: ContactCreateBody,
  patch: ResourcePatchOf<'contact-details'>
): void {
  const key = contactDetailColumnByType[patch.detailType];
  body[key] = patch.items.map(item => {
    assertNoPersistedId(item.id, `buildCreateBody(${key})`);
    return {
      detail_type: patch.detailType,
      value_1: item.value,
      value_2: item.type,
      ...(item.country ? { value_3: item.country } : {})
    };
  });
}

function tagRows(patch: ResourcePatchOf<'tags'>): TagBodyRow[] {
  return [
    ...patch.ids.map(id => ({
      id: assertPersistedId(id, 'buildCreateBody(tags)')
    })),
    ...patch.create.map(tag => ({ tag, tag_type: 'resource' as const }))
  ];
}

function groupRows(
  patch: ResourcePatchOf<'groups'>,
  resourceTypeId: number
): GroupBodyRow[] {
  return [
    ...patch.ids.map(id => ({
      id: assertPersistedId(id, 'buildCreateBody(groups)')
    })),
    ...patch.create.map(name => ({ name, entity_id: resourceTypeId }))
  ];
}

function applyRates(
  body: ContactCreateBody,
  patch: ResourcePatchOf<'rates'>
): void {
  // `rate_id` references an existing `rates` row (a plain FK column, not a
  // graph relation), so a card row is fine on create; only the contact-owned
  // `contact_rates.id` must be absent.
  body.rates = patch.items.map((item, index) => {
    const context = `buildCreateBody(rates)[${index}]`;
    assertNoPersistedId(item.id, context);
    return { ...rateValueColumns(item, context), order: index + 1 };
  });
}

function applyCustomField(
  body: ContactCreateBody,
  patch: ResourcePatchOf<'custom-field'>
): void {
  // Nothing exists yet, so clearing is a no-op on create.
  if (patch.write.type === 'clear') return;
  body.custom_fields = [
    ...(body.custom_fields ?? []),
    customFieldRowWithoutId(
      patch.definitionId,
      valueScopeUtils.toWorkspaceId(patch.scope),
      patch.write.columns
    )
  ];
}

function applyCustomFieldGroup(
  body: ContactCreateBody,
  patch: ResourcePatchOf<'custom-field-group'>
): void {
  const context = `buildCreateBody(custom-field-group ${patch.definitionId})`;
  const workspaceId = valueScopeUtils.toWorkspaceId(patch.scope);
  const rows = patch.rows.map((row, index) => {
    assertNoPersistedId(row.id, context);
    return {
      custom_fieldgroup_definition_id: patch.definitionId,
      archived: false as const,
      order: index + 1,
      custom_fields: row.members.map(member => {
        assertNoPersistedId(
          member.id,
          `${context} member ${member.definitionId}`
        );
        return customFieldRowWithoutId(
          member.definitionId,
          workspaceId,
          member.columns
        );
      })
    };
  });
  body.custom_fieldgroups = [...(body.custom_fieldgroups ?? []), ...rows];
}
